import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DataGrid, GridColDef, esES } from "@mui/x-data-grid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { tokens, useAuth } from "@/utilities";
import careerRedirectsService, {
  CareerRedirectEvent,
  CareerRedirectSummary,
} from "@/api/careerRedirects.service";
import instituteService from "@/api/institute.service";

export const CareerRedirectsDetail: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { careerId } = useParams<{ careerId: string }>();

  const { user, institute } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [careerInfo, setCareerInfo] = useState<CareerRedirectSummary | null>(
    (location.state as any)?.career || null
  );
  const [allEvents, setAllEvents] = useState<CareerRedirectEvent[]>(
    (location.state as any)?.career?.events || []
  );

  // Estados para filtro de fechas
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [appliedStartDate, setAppliedStartDate] = useState<string>("");
  const [appliedEndDate, setAppliedEndDate] = useState<string>("");

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [actualSubscriptionLevel, setActualSubscriptionLevel] = useState<number>(
    institute?.subscriptionLevel ?? 0
  );

  // 1. Validar suscripción desde /institutes/{:id}/ si no está en contexto
  const uidInstitution = user?.userData.institute;
  useEffect(() => {
    if (institute && typeof institute.subscriptionLevel === "number") {
      setActualSubscriptionLevel(institute.subscriptionLevel);
    } else if (uidInstitution) {
      instituteService.getById(uidInstitution).then((instDoc) => {
        if (instDoc && typeof instDoc.subscriptionLevel === "number") {
          setActualSubscriptionLevel(instDoc.subscriptionLevel);
        }
      });
    }
  }, [institute, uidInstitution]);

  // Validar rol: sólo Administrador IES o Súper Administrador
  const role = user?.userData.role;
  const isAuthorizedRole =
    role === "Administrador IES" || role === "Super Administrador";
  const hasLicense = actualSubscriptionLevel > 2;

  const isGeneral = careerId === "general" || !careerId;

  // 2. Cargar eventos si no vinieron en location.state o se refresca la página
  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      if (!isAuthorizedRole || !hasLicense || !uidInstitution) {
        setLoading(false);
        return;
      }

      // Si ya tenemos los eventos precargados de state (para una carrera específica), sólo detenemos loading
      if (!isGeneral && allEvents.length > 0 && careerInfo) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await careerRedirectsService.getRedirects(uidInstitution, 90);

        if (!isMounted) return;

        if (isGeneral) {
          setCareerInfo({
            id: "general",
            careerId: "Todas",
            facultyId: "",
            facultyName: "Todas las Facultades",
            careerName: "Todas las Carreras",
            redirectCount: result.allEvents.length,
            events: result.allEvents,
          });
          setAllEvents(result.allEvents);
        } else {
          // Buscar el summary que corresponda a careerId o al id
          const matched = result.summaries.find(
            (s) =>
              s.careerId === careerId ||
              s.id === careerId ||
              s.careerName.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
          );

          if (matched) {
            setCareerInfo(matched);
            setAllEvents(matched.events);
          } else {
            // Si no se encuentra en summary, filtrar en allEvents
            const filtered = result.allEvents.filter(
              (ev) =>
                ev.career_id === careerId ||
                ev.career_name.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
            );
            setAllEvents(filtered);
            if (filtered.length > 0) {
              setCareerInfo({
                id: careerId || "carrera",
                careerId: filtered[0].career_id || careerId || "N/A",
                facultyId: filtered[0].faculty_id || "",
                facultyName: filtered[0].faculty_name || "Facultad",
                careerName: filtered[0].career_name || "Carrera",
                redirectCount: filtered.length,
                events: filtered,
              });
            }
          }
        }
      } catch (err) {
        console.error("Error al cargar detalle de redirecciones:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void fetchData();

    return () => {
      isMounted = false;
    };
  }, [uidInstitution, careerId, isAuthorizedRole, hasLicense, isGeneral]);

  // Manejadores del filtro de fechas (consulta bajo demanda a BigQuery sin caché)
  const handleApplyFilter = async () => {
    if (!startDate && !endDate) return;
    if (!uidInstitution) return;

    let finalStart = startDate;
    let finalEnd = endDate;

    if (finalStart && !finalEnd) {
      finalEnd = new Date().toISOString().slice(0, 10);
      setEndDate(finalEnd);
    } else if (!finalStart && finalEnd) {
      const d = new Date(finalEnd);
      d.setDate(d.getDate() - 90);
      finalStart = d.toISOString().slice(0, 10);
      setStartDate(finalStart);
    }

    if (finalStart > finalEnd) {
      const temp = finalStart;
      finalStart = finalEnd;
      finalEnd = temp;
      setStartDate(finalStart);
      setEndDate(finalEnd);
    }

    try {
      setLoading(true);
      // Consulta bajo demanda directa a la Cloud Function / BigQuery sin usar caché
      const result = await careerRedirectsService.getRedirects(uidInstitution, {
        startDate: finalStart,
        endDate: finalEnd,
        forceRefresh: true,
      });

      setAppliedStartDate(finalStart);
      setAppliedEndDate(finalEnd);

      if (isGeneral) {
        setCareerInfo({
          id: "general",
          careerId: "Todas",
          facultyId: "",
          facultyName: "Todas las Facultades",
          careerName: "Todas las Carreras",
          redirectCount: result.allEvents.length,
          events: result.allEvents,
        });
        setAllEvents(result.allEvents);
      } else {
        const matched = result.summaries.find(
          (s) =>
            s.careerId === careerId ||
            s.id === careerId ||
            s.careerName.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
        );

        if (matched) {
          setCareerInfo(matched);
          setAllEvents(matched.events);
        } else {
          const filtered = result.allEvents.filter(
            (ev) =>
              ev.career_id === careerId ||
              ev.career_name.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
          );
          setAllEvents(filtered);
          setCareerInfo((prev) => ({
            id: careerId || "carrera",
            careerId: filtered[0]?.career_id || prev?.careerId || careerId || "N/A",
            facultyId: filtered[0]?.faculty_id || prev?.facultyId || "",
            facultyName: filtered[0]?.faculty_name || prev?.facultyName || "Facultad",
            careerName: filtered[0]?.career_name || prev?.careerName || "Carrera",
            redirectCount: filtered.length,
            events: filtered,
          }));
        }
      }
    } catch (err) {
      console.error("Error al consultar redirecciones bajo demanda:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilter = async () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    if (!uidInstitution) return;

    try {
      setLoading(true);
      // Recargar consulta por defecto (últimos 90 días)
      const result = await careerRedirectsService.getRedirects(uidInstitution, 90);

      if (isGeneral) {
        setCareerInfo({
          id: "general",
          careerId: "Todas",
          facultyId: "",
          facultyName: "Todas las Facultades",
          careerName: "Todas las Carreras",
          redirectCount: result.allEvents.length,
          events: result.allEvents,
        });
        setAllEvents(result.allEvents);
      } else {
        const matched = result.summaries.find(
          (s) =>
            s.careerId === careerId ||
            s.id === careerId ||
            s.careerName.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
        );

        if (matched) {
          setCareerInfo(matched);
          setAllEvents(matched.events);
        } else {
          const filtered = result.allEvents.filter(
            (ev) =>
              ev.career_id === careerId ||
              ev.career_name.toLowerCase() === decodeURIComponent(careerId || "").toLowerCase()
          );
          setAllEvents(filtered);
          if (filtered.length > 0) {
            setCareerInfo({
              id: careerId || "carrera",
              careerId: filtered[0].career_id || careerId || "N/A",
              facultyId: filtered[0].faculty_id || "",
              facultyName: filtered[0].faculty_name || "Facultad",
              careerName: filtered[0].career_name || "Carrera",
              redirectCount: filtered.length,
              events: filtered,
            });
          }
        }
      }
    } catch (err) {
      console.error("Error al restablecer filtro:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrado de eventos en memoria por rango de fechas (respaldo de orden y límites)
  const filteredEvents = useMemo(() => {
    return allEvents
      .filter((ev) => {
        const evDate = new Date(ev.timestamp);

        if (appliedStartDate) {
          const start = new Date(`${appliedStartDate}T00:00:00`);
          if (evDate < start) return false;
        }

        if (appliedEndDate) {
          const end = new Date(`${appliedEndDate}T23:59:59.999`);
          if (evDate > end) return false;
        }

        return true;
      })
      .sort((a, b) => b.timestamp - a.timestamp); // Orden descendente por fecha
  }, [allEvents, appliedStartDate, appliedEndDate]);

  // Funciones de descarga Excel y CSV
  const handleExport = (format: "xlsx" | "csv") => {
    if (filteredEvents.length === 0) return;

    const dataToExport = filteredEvents.map((ev) => {
      const dateObj = new Date(ev.timestamp);
      return {
        "Fecha": dateObj.toLocaleString("es-CO"),
        "ID Carrera": ev.career_id || careerInfo?.careerId || "N/A",
        "Nombre Carrera": ev.career_name || careerInfo?.careerName || "",
        "Facultad": ev.faculty_name || careerInfo?.facultyName || "",
        "Correo Electrónico": ev.user_email || "Anónimo",
        "URL de Redirección": ev.siteUrl || "",
        "Nombre Usuario": ev.user_object?.nombre || "-",
        "Apellido Usuario": ev.user_object?.apellido || "-",
        "Teléfono Usuario": ev.user_object?.telefono || "-",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Redirecciones");

    const safeName = (careerInfo?.careerName || "Carrera")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .slice(0, 30);
    const dateStamp = new Date().toISOString().slice(0, 10);
    const fileName = `Redirecciones_${safeName}_${dateStamp}.${format}`;

    XLSX.writeFile(workbook, fileName, {
      bookType: format,
    });
  };

  // Validación de acceso
  if (!isAuthorizedRole || !hasLicense) {
    return (
      <Box sx={{ m: "30px", p: "30px", textAlign: "center" }}>
        <Typography variant="h5" color="error" fontWeight="600">
          No tienes permisos para acceder a esta sección o tu institución no cuenta con el nivel de licencia requerido.
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: "20px", backgroundColor: "#3F51B5" }}
          onClick={() => navigate("/dashboard")}
        >
          Volver al Dashboard
        </Button>
      </Box>
    );
  }

  // Definición de columnas de la tabla de eventos detallados
  const columns: GridColDef[] = useMemo(() => {
    const baseCols: GridColDef[] = [
      {
        field: "fecha",
        headerName: "Fecha",
        flex: 1.5,
        minWidth: 170,
        renderCell: (params) => {
          const dateObj = new Date(params.row.timestamp);
          return (
            <Typography variant="body2" sx={{ color: colors.grey[100] }}>
              {isNaN(dateObj.getTime())
                ? params.value
                : dateObj.toLocaleString("es-CO", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
            </Typography>
          );
        },
      },
    ];

    if (isGeneral) {
      baseCols.push(
        {
          field: "career_id",
          headerName: "ID Carrera",
          flex: 1,
          minWidth: 110,
          renderCell: (params) => (
            <Typography variant="body2" fontWeight="bold" sx={{ color: colors.grey[100] }}>
              {params.value || "N/A"}
            </Typography>
          ),
        },
        {
          field: "career_name",
          headerName: "Nombre Carrera",
          flex: 2,
          minWidth: 190,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ color: colors.grey[100] }}>
              {params.value || "-"}
            </Typography>
          ),
        },
        {
          field: "faculty_name",
          headerName: "Facultad",
          flex: 1.8,
          minWidth: 180,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ color: colors.grey[100] }}>
              {params.value || "Facultad no especificada"}
            </Typography>
          ),
        }
      );
    }

    baseCols.push(
      {
        field: "user_email",
        headerName: "Correo Electrónico",
        flex: 2,
        minWidth: 190,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[100] }}>
            {params.value && params.value !== "ANON" ? params.value : "Anónimo"}
          </Typography>
        ),
      },
      {
        field: "siteUrl",
        headerName: "URL de Redirección",
        flex: 2.3,
        minWidth: 230,
        renderCell: (params) => {
          const url = params.value;
          if (!url) return <Typography variant="body2">-</Typography>;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Typography
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: colors.blueAccent[400],
                  textDecoration: "underline",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "210px",
                  "&:hover": { color: colors.blueAccent[300] },
                }}
              >
                {url}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: "14px", color: colors.blueAccent[400] }} />
            </Box>
          );
        },
      },
      {
        field: "nombre",
        headerName: "Nombre",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (params) => params.row.user_object?.nombre || "-",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[100] }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "apellido",
        headerName: "Apellido",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (params) => params.row.user_object?.apellido || "-",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[100] }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "telefono",
        headerName: "Teléfono",
        flex: 1.2,
        minWidth: 120,
        valueGetter: (params) => params.row.user_object?.telefono || "-",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[100] }}>
            {params.value}
          </Typography>
        ),
      }
    );

    return baseCols;
  }, [isGeneral, colors]);

  return (
    <Box sx={{ m: "20px" }}>
      {/* BOTÓN VOLVER AL DASHBOARD */}
      <Box sx={{ mb: "20px" }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{
            backgroundColor: "#3F51B5",
            color: "#ffffff",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: "600",
            padding: "8px 18px",
            borderRadius: "5px",
            "&:hover": { backgroundColor: colors.blueAccent[500] },
          }}
          onClick={() => navigate("/dashboard")}
        >
          Volver al Dashboard
        </Button>
      </Box>

      {/* TARJETA DE INFORMACIÓN DEL PROGRAMA */}
      <Paper
        elevation={2}
        sx={{
          backgroundColor: colors.primary[400],
          p: "20px",
          borderRadius: "6px",
          mb: "20px",
          borderLeft: `6px solid #007FC0`,
        }}
      >
        <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} sx={{ mb: "10px" }}>
          Detalle de Redirecciones - {careerInfo?.careerName || "Programa Académico"}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: "15px",
            mt: "15px",
          }}
        >
          <Box>
            <Typography variant="body2" color={colors.grey[300]}>
              ID Carrera:
            </Typography>
            <Typography variant="h6" fontWeight="600" color={colors.grey[100]}>
              {careerInfo?.careerId || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color={colors.grey[300]}>
              Facultad:
            </Typography>
            <Typography variant="h6" fontWeight="600" color={colors.grey[100]}>
              {careerInfo?.facultyName || "No especificada"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color={colors.grey[300]}>
              {appliedStartDate || appliedEndDate ? "Período seleccionado:" : "Total Redirecciones (90 días):"}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color={colors.greenAccent[500]}>
              {appliedStartDate || appliedEndDate
                ? `${appliedStartDate || "..."} a ${appliedEndDate || "hoy"}`
                : allEvents.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color={colors.grey[300]}>
              {appliedStartDate || appliedEndDate ? "Redirecciones encontradas:" : "Eventos Filtrados:"}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="#007FC0">
              {filteredEvents.length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* BARRA DE FILTROS DE FECHA Y EXPORTACIÓN */}
      <Paper
        elevation={2}
        sx={{
          backgroundColor: colors.primary[400],
          p: "20px",
          borderRadius: "6px",
          mb: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "15px",
          }}
        >
          {/* CONTROLES DE FECHA */}
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
            <TextField
              label="Fecha Inicio"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: "170px",
                "& .MuiInputBase-root": { color: colors.grey[100] },
                "& .MuiInputLabel-root": { color: colors.grey[200] },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
              }}
            />
            <TextField
              label="Fecha Fin"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: "170px",
                "& .MuiInputBase-root": { color: colors.grey[100] },
                "& .MuiInputLabel-root": { color: colors.grey[200] },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[400] },
              }}
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} sx={{ color: "#ffffff" }} /> : <FilterAltIcon />}
              disabled={loading || (!startDate && !endDate)}
              onClick={handleApplyFilter}
              sx={{
                backgroundColor: "#007FC0",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#006294" },
                "&.Mui-disabled": { backgroundColor: colors.grey[700], color: colors.grey[400] },
              }}
            >
              {loading ? "Consultando..." : "Mostrar"}
            </Button>
            {(appliedStartDate || appliedEndDate) && (
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilter}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.grey[400],
                  textTransform: "none",
                  "&:hover": { borderColor: colors.grey[200] },
                }}
              >
                Limpiar filtro
              </Button>
            )}
          </Box>

          {/* BOTONES DE DESCARGA */}
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={filteredEvents.length === 0}
              onClick={() => handleExport("xlsx")}
              sx={{
                backgroundColor: "#2e7d32",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#1b5e20" },
                "&.Mui-disabled": { backgroundColor: colors.grey[700], color: colors.grey[400] },
              }}
            >
              Descargar Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={filteredEvents.length === 0}
              onClick={() => handleExport("csv")}
              sx={{
                backgroundColor: "#0288d1",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#01579b" },
                "&.Mui-disabled": { backgroundColor: colors.grey[700], color: colors.grey[400] },
              }}
            >
              Descargar CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* TABLA DE DETALLES */}
      <Paper
        elevation={2}
        sx={{
          backgroundColor: colors.primary[400],
          p: "20px",
          borderRadius: "6px",
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: `1px solid ${colors.primary[500]}` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#007FC0",
            borderBottom: "none",
            color: "#fcfcfc",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: "#007FC0",
            color: "#ffffff",
          },
          "& .MuiTablePagination-root": { color: "#ffffff" },
          "& .MuiSvgIcon-root": { color: "#ffffff" },
        }}
      >
        <Box sx={{ mb: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
            Historial de Redirecciones
          </Typography>
          <Typography variant="body2" color={colors.grey[200]}>
            Ordenado de más reciente a más antiguo
          </Typography>
        </Box>
        <Divider sx={{ mb: "15px", borderColor: colors.primary[500] }} />

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              gap: "12px",
            }}
          >
            <CircularProgress sx={{ color: "#3F51B5" }} />
            <Typography variant="body1" color={colors.grey[100]}>
              Cargando historial de eventos...
            </Typography>
          </Box>
        ) : filteredEvents.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "150px",
            }}
          >
            <Typography variant="h6" color={colors.grey[200]}>
              No se encontraron eventos de redirección para este período.
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filteredEvents}
            columns={columns}
            autoHeight
            initialState={{
              sorting: {
                sortModel: [{ field: "fecha", sort: "desc" }],
              },
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            getRowId={(row) => row.id}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CareerRedirectsDetail;
